import { DatePipe, DecimalPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { switchMap } from 'rxjs/operators'

import {
    BaseComponent,
    CREW_SCHEDULE_CONTEXT_DAYS,
    CREW_SCHEDULE_WEEKDAYS,
    CrewMemberReassignment,
    CrewScheduleContext,
    CrewScheduleDayAssignment,
    CrewScheduleForeman,
    CrewScheduleForemanState,
    CrewScheduleMember,
    CrewScheduleSegment,
    Division,
    FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY,
    ForemanScheduleGroupBy,
    ForemanSegmentSpan,
    FullCrewScheduleProjection,
    FullScheduleSegmentInput,
    IsoDate,
    JobPhaseOption,
    JobPhasePool,
    ProjectedSegment,
    SavedForemanSegment,
    SavedWeekendEntry,
    TimeOffEntry,
    VpJob,
    addDaysIso,
    buildJobOrderIndex,
    collectWeekdaysFrom,
    compareForemanScheduleJobGroups,
    compactSegmentOverlapNote,
    crewScheduleJobPhaseKey,
    daysBetweenIso,
    describeSegmentOverlaps,
    findSegmentOverlaps,
    isoDateStringForApi,
    resolveEffectiveMembers,
    todayIso,
    weekendDatesBetween,
    ModalBackDrop,
    ModalSize,
} from '../../../shared'
import { AutoSizeDirective } from '../../../shared/utility/auto-size.directive'
import {
    ForemanScheduleJobAssignmentModalComponent,
    JobAssignmentModalResult,
} from '../foreman-schedule-job-assignment-modal.component'

export type CrewScheduleGridColumn =
    | { type: 'day'; key: string; date: IsoDate; label: string; isToday: boolean }
    | { type: 'weekend'; key: string; dates: IsoDate[] }
    | { type: 'weekend-day'; key: string; date: IsoDate; label: string; weekendKey: string }

/** A crew member who is off on a given day, surfaced as a chip in the day column. */
export interface CrewOffMemberVm {
    hrRef: number
    name: string
    initials: string
    status: 'Approved' | 'Pending'
    leaveType: string
}

/** Lazy per-cell job/phase picker state, used for both weekend work and weekday assignment placement. */
export interface WeekendPickerState {
    jobId: string
    phase: string
    phases: JobPhaseOption[]
    loadingPhases: boolean
    /** Optional forced day count entered in the day picker before the assignment is confirmed. */
    days?: number | null
}

export interface CrewScheduleGridCellVm {
    type: 'day' | 'weekend' | 'weekend-day'
    key: string
    date?: IsoDate
    isToday?: boolean
    isHoliday?: boolean
    holidayName?: string | null
    assignment?: CrewScheduleDayAssignment | null
    segmentId?: string | null
    isOverEstimate?: boolean
    /** True when this day was scheduled past the phase's remaining hours via a forced day count. */
    isOverHours?: boolean
    isShared?: boolean
    showClearButton?: boolean
    /** True when this empty weekday cell can offer an "add assignment" affordance. */
    showAddButton?: boolean
    /** Open job/phase picker for placing a new assignment starting on this weekday, if any. */
    dayPicker?: WeekendPickerState | null
    weekendKey?: string
    weekendHasEntries?: boolean
    weekendEntry?: SavedWeekendEntry | null
    weekendPicker?: WeekendPickerState | null
    weekendDefaultHours?: number
    offMembers?: CrewOffMemberVm[]
    /** Set when this cell is masked because it belongs to another job in a job-grouped view. */
    maskedJobId?: string | null
}

export interface CrewScheduleForemanRowVm {
    hrRef: number
    name: string
    jobTitle: string
    divisionId: string | null
    divisionName: string | null
    expanded: boolean
    totalCrewCount: number
    scheduledOffDays: number
    scheduledOffDaysLabel: string
    state: CrewScheduleForemanState
    summaryText: string
    warningText: string | null
    gridCells: CrewScheduleGridCellVm[]
}

/** A division or job grouping of foreman rows for the crew schedule template. */
export interface CrewScheduleForemanGroupVm {
    key: string
    label: string
    groupJobId: string | null
    divisionId: string | null
    rows: CrewScheduleForemanRowVm[]
}

function newSegmentId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `seg-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

@Component({
    selector: 'app-foreman-schedule-crew-schedule-tab',
    templateUrl: './foreman-schedule-crew-schedule-tab.component.html',
    styleUrls: ['./foreman-schedule-crew-schedule-tab.component.css'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, DecimalPipe, FormsModule, AutoSizeDirective],
})
export class ForemanScheduleCrewScheduleTabComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)
    private static readonly GROUP_BY_STORAGE_KEY = 'foremanSchedule.crewScheduleGroupBy'

    readonly isActive = input(false)
    readonly activeJobs = input.required<VpJob[]>()
    readonly canEdit = input(true)
    readonly reassignments = input<CrewMemberReassignment[]>([])
    readonly savedSegments = input<SavedForemanSegment[]>([])
    readonly savedWeekendEntries = input<SavedWeekendEntry[]>([])
    /** Bumped by the shell when the foreman roster changes; triggers a context reload. */
    readonly foremanRevision = input(0)

    readonly isLoading = signal(false)
    readonly loadError = signal<string | null>(null)
    readonly context = signal<CrewScheduleContext | null>(null)
    readonly expandedForemanHrRefs = signal<Set<number>>(new Set())
    readonly foremanStates = signal<Map<number, CrewScheduleForemanState>>(new Map())
    /** Per job+phase shared pool summary from the latest full projection, keyed by job|phase. */
    readonly projectionPools = signal<Map<string, JobPhasePool>>(new Map())
    /** Saved manual weekend work, keyed by foreman hrRef. */
    readonly weekendEntriesByForeman = signal<Map<number, SavedWeekendEntry[]>>(new Map())
    /** Lazy weekend job/phase picker state, keyed by `${foremanHrRef}|${date}`. */
    readonly weekendPickers = signal<Map<string, WeekendPickerState>>(new Map())
    /** Lazy weekday job/phase picker state for placing a new assignment, keyed by `${foremanHrRef}|${date}`. */
    readonly dayPickers = signal<Map<string, WeekendPickerState>>(new Map())
    /** Weekend column keys (`wknd-<date>`) currently expanded into day columns. */
    readonly expandedWeekends = signal<Set<string>>(new Set())
    /** When true, phase pickers also list phases that have no remaining hours (session-only). */
    readonly includeAllPhases = signal(false)

    /** Active divisions for the toolbar filter dropdown, sorted by name. */
    readonly divisionOptions = signal<Division[]>([])
    /** Selected division filter; `''` means All (show every foreman). */
    readonly selectedDivisionId = signal('')

    /** Toolbar grouping mode; defaults to division, persisted per tab in localStorage. */
    readonly groupBy = signal<ForemanScheduleGroupBy>(this.readStoredGroupBy())

    /** Exposed for template label. */
    protected readonly crewScheduleWeekdayCount = CREW_SCHEDULE_WEEKDAYS

    private hasLoaded = false
    private reassignmentsSnapshot = ''
    private savedSegmentsSnapshot = ''
    private savedWeekendEntriesSnapshot = ''
    private foremanRevisionSnapshot = 0

    readonly gridStart = computed(() => collectWeekdaysFrom(todayIso(), 1)[0] ?? todayIso())

    readonly gridWeekdays = computed(() => collectWeekdaysFrom(todayIso(), CREW_SCHEDULE_WEEKDAYS))

    readonly gridColumns = computed((): CrewScheduleGridColumn[] => {
        const weekdays = this.gridWeekdays()
        const cols: CrewScheduleGridColumn[] = []
        const today = todayIso()
        const expanded = this.expandedWeekends()

        for (let i = 0; i < weekdays.length; i++) {
            const date = weekdays[i]
            cols.push({
                type: 'day',
                key: date,
                date,
                label: this.formatShortDate(date),
                isToday: date === today,
            })
            const next = weekdays[i + 1]
            if (i < weekdays.length - 1 && daysBetweenIso(date, next) > 1) {
                const key = `wknd-${date}`
                const dates = weekendDatesBetween(date, next)
                if (expanded.has(key)) {
                    for (const wd of dates) {
                        cols.push({
                            type: 'weekend-day',
                            key: `wknd-day-${wd}`,
                            date: wd,
                            label: this.formatShortDate(wd),
                            weekendKey: key,
                        })
                    }
                } else {
                    cols.push({ type: 'weekend', key, dates })
                }
            }
        }
        return cols
    })

    readonly gridTemplateColumns = computed(() => {
        const parts = ['12rem']
        for (const col of this.gridColumns()) {
            parts.push(col.type === 'weekend' ? '1.725rem' : '11.25rem')
        }
        return parts.join(' ')
    })

    /** Weekend dates that have at least one saved entry (any foreman); drives the collapsed indicator. */
    readonly weekendDatesWithEntries = computed(() => {
        const set = new Set<IsoDate>()
        for (const entries of this.weekendEntriesByForeman().values()) {
            for (const e of entries) set.add(e.date)
        }
        return set
    })

    readonly gridRangeEnd = computed(() => {
        const weekdays = this.gridWeekdays()
        return weekdays.length ? weekdays[weekdays.length - 1] : todayIso()
    })

    readonly holidayByDate = computed(() => {
        const map = new Map<IsoDate, string>()
        for (const h of this.context()?.holidays ?? []) {
            const date = isoDateStringForApi(h.date as string | Date | null | undefined)
            if (date) map.set(date, h.name)
        }
        return map
    })

    readonly foremanRows = computed((): CrewScheduleForemanRowVm[] => {
        const ctx = this.context()
        if (!ctx) return []
        const expanded = this.expandedForemanHrRefs()
        const states = this.foremanStates()
        const holidays = this.holidayByDate()
        const canEdit = this.canEdit()
        const gridColumns = this.gridColumns()
        const weekdays = this.gridWeekdays()
        const reassignments = this.reassignments()
        const gridStart = this.gridStart()
        const weekendEntriesByForeman = this.weekendEntriesByForeman()
        const weekendPickers = this.weekendPickers()
        const dayPickers = this.dayPickers()

        return ctx.foremen.map(f => {
            const state = states.get(f.hrRef) ?? this.createEmptyForemanState()
            const assignmentByDate = new Map<
                IsoDate,
                { assignment: CrewScheduleDayAssignment; segmentId: string; overThresholdHours: number }
            >()

            for (const segment of state.segments) {
                for (const day of segment.days) {
                    const date = isoDateStringForApi(day.date as string | Date | null | undefined)
                    assignmentByDate.set(date, {
                        assignment: { ...day, date },
                        segmentId: segment.id,
                        overThresholdHours: segment.overThresholdHours,
                    })
                }
            }

            const gridCells: CrewScheduleGridCellVm[] = []

            const foremanWeekendEntries = weekendEntriesByForeman.get(f.hrRef) ?? []

            for (const col of gridColumns) {
                if (col.type === 'weekend') {
                    gridCells.push({
                        type: 'weekend',
                        key: col.key,
                        weekendKey: col.key,
                        weekendHasEntries: foremanWeekendEntries.some(e => col.dates.includes(e.date)),
                    })
                    continue
                }

                if (col.type === 'weekend-day') {
                    const wDate = col.date
                    const entry = foremanWeekendEntries.find(e => e.date === wDate) ?? null
                    const pickerKey = this.weekendPickerKey(f.hrRef, wDate)
                    const picker =
                        entry || !canEdit
                            ? null
                            : weekendPickers.get(pickerKey) ?? { jobId: '', phase: '', phases: [], loadingPhases: false }
                    const effectiveCrew = resolveEffectiveMembers(f, wDate, ctx.foremen, reassignments).length
                    gridCells.push({
                        type: 'weekend-day',
                        key: col.key,
                        date: wDate,
                        weekendKey: col.weekendKey,
                        isHoliday: !!holidays.get(wDate),
                        holidayName: holidays.get(wDate) ?? null,
                        weekendEntry: entry,
                        weekendPicker: picker,
                        weekendDefaultHours: effectiveCrew * 10,
                        showClearButton: canEdit && !!entry,
                    })
                    continue
                }

                const date = col.date
                const holidayName = holidays.get(date) ?? null
                const mapped = assignmentByDate.get(date) ?? null
                const assignment = mapped?.assignment ?? null
                const segmentId = mapped?.segmentId ?? null
                const isOverEstimate =
                    !!assignment &&
                    assignment.hoursScheduled > 0 &&
                    (mapped?.overThresholdHours ?? 0) > 0 &&
                    assignment.remainingHoursAtEnd < (mapped?.overThresholdHours ?? 0) - 0.001
                const isOverHours = !!assignment && assignment.hoursScheduled > 0 && !!assignment.isOverHours
                const isShared = !!assignment && assignment.hoursScheduled > 0 && (assignment.sharedCrewCount ?? 1) > 1
                const isHoliday = !!holidayName || !!assignment?.isHoliday
                // Any open weekday (no projected work and not a holiday) can start a new assignment.
                const isEmptyWorkday = (!assignment || assignment.hoursScheduled <= 0) && !isHoliday
                const dayPicker = canEdit && isEmptyWorkday ? dayPickers.get(this.dayPickerKey(f.hrRef, date)) ?? null : null

                gridCells.push({
                    type: 'day',
                    key: col.key,
                    date,
                    isToday: col.isToday,
                    isHoliday,
                    holidayName: assignment?.holidayName ?? holidayName,
                    assignment,
                    segmentId,
                    isOverEstimate,
                    isOverHours,
                    isShared,
                    showAddButton: canEdit && isEmptyWorkday && !dayPicker,
                    dayPicker,
                    showClearButton: canEdit && !!assignment && assignment.hoursScheduled > 0 && !!segmentId,
                    offMembers: this.buildOffMembers(f, date, ctx.foremen, reassignments),
                })
            }

            const scheduledOffDays = this.countCrewScheduledOffDays(f, ctx.foremen, weekdays, reassignments)
            const effectiveCrewCount = resolveEffectiveMembers(f, gridStart, ctx.foremen, reassignments).length

            return {
                hrRef: f.hrRef,
                name: f.name,
                jobTitle: f.jobTitle,
                divisionId: f.divisionId ?? null,
                divisionName: f.divisionName ?? null,
                expanded: expanded.has(f.hrRef),
                totalCrewCount: effectiveCrewCount,
                scheduledOffDays,
                scheduledOffDaysLabel: this.formatScheduledOffDaysLabel(scheduledOffDays),
                state,
                summaryText: this.buildSummaryText(state),
                warningText: this.buildWarningText(state),
                gridCells,
            }
        })
    })

    /** Rows after applying the toolbar division filter (`''` = All). */
    readonly filteredForemanRows = computed((): CrewScheduleForemanRowVm[] => {
        const selected = this.selectedDivisionId()
        const rows = this.foremanRows()
        if (!selected) return rows
        return rows.filter(row => row.divisionId === selected)
    })

    /** Filtered rows grouped by division or job. */
    readonly foremanGroups = computed((): CrewScheduleForemanGroupVm[] => {
        const rows = this.filteredForemanRows()
        if (this.groupBy() === 'job') {
            return this.buildJobForemanGroups(rows)
        }
        return this.buildDivisionForemanGroups(rows)
    })

    readonly hasVisibleForemen = computed(() => this.filteredForemanRows().length > 0)

    constructor() {
        super()
        effect(() => {
            if (this.isActive() && !this.hasLoaded) {
                this.loadContext()
            }
        })

        effect(() => {
            const snapshot = JSON.stringify(this.reassignments())
            if (snapshot === this.reassignmentsSnapshot) return
            this.reassignmentsSnapshot = snapshot
            if (!this.hasLoaded) return
            this.reprojectAll()
        })

        // Seed the weekend ledger before segments rebuild so weekday projections account for weekend hours.
        effect(() => {
            const saved = this.savedWeekendEntries()
            const ctx = this.context()
            if (!ctx) return
            const snapshot = JSON.stringify(saved)
            if (snapshot === this.savedWeekendEntriesSnapshot) return
            this.savedWeekendEntriesSnapshot = snapshot
            this.seedWeekendEntries(saved)
        })

        effect(() => {
            const saved = this.savedSegments()
            const ctx = this.context()
            if (!ctx) return
            const snapshot = JSON.stringify(saved)
            if (snapshot === this.savedSegmentsSnapshot) return
            this.savedSegmentsSnapshot = snapshot
            this.rebuildSavedSegments(saved)
        })

        effect(() => {
            const revision = this.foremanRevision()
            if (revision === this.foremanRevisionSnapshot) return
            this.foremanRevisionSnapshot = revision
            if (!this.hasLoaded) return
            if (this.isActive()) {
                this.refresh()
            } else {
                // Reload the next time the tab is activated.
                this.hasLoaded = false
            }
        })
    }

    refresh() {
        this.hasLoaded = false
        this.savedSegmentsSnapshot = ''
        this.savedWeekendEntriesSnapshot = ''
        this.projectionPools.set(new Map())
        this.foremanStates.set(new Map())
        this.weekendEntriesByForeman.set(new Map())
        this.weekendPickers.set(new Map())
        this.dayPickers.set(new Map())
        this.expandedWeekends.set(new Set())
        this.loadContext()
    }

    /** Sets the toolbar division filter (`''` = All). */
    onDivisionFilterChange(value: string) {
        this.selectedDivisionId.set(value ?? '')
    }

    onGroupByChange(value: ForemanScheduleGroupBy) {
        const mode: ForemanScheduleGroupBy = value === 'job' ? 'job' : 'division'
        this.groupBy.set(mode)
        try {
            localStorage.setItem(ForemanScheduleCrewScheduleTabComponent.GROUP_BY_STORAGE_KEY, mode)
        } catch {
            /* ignore storage failures */
        }
    }

    /** Toggles whether phase pickers include phases with no remaining hours, refetching any open picker. */
    onIncludeAllPhasesChange(value: boolean) {
        this.includeAllPhases.set(value)

        for (const [key, picker] of this.dayPickers()) {
            const jobId = this.normalizeJobId(picker.jobId)
            if (!jobId) continue
            const [hrRefStr, date] = key.split('|')
            const hrRef = Number(hrRefStr)
            if (hrRef) this.onDayJobChange(hrRef, date, jobId)
        }

        for (const [key, picker] of this.weekendPickers()) {
            const jobId = this.normalizeJobId(picker.jobId)
            if (!jobId) continue
            const [hrRefStr, date] = key.split('|')
            const hrRef = Number(hrRefStr)
            if (hrRef) this.onWeekendJobChange(hrRef, date, jobId)
        }
    }

    toggleForeman(hrRef: number) {
        this.expandedForemanHrRefs.update(set => {
            const next = new Set(set)
            if (next.has(hrRef)) next.delete(hrRef)
            else next.add(hrRef)
            return next
        })
    }

    /** Expands/collapses a weekend column (`wknd-<date>`) into editable Sat/Sun day columns. */
    toggleWeekend(key: string) {
        this.expandedWeekends.update(set => {
            const next = new Set(set)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    weekendHasEntries(dates: IsoDate[]): boolean {
        const set = this.weekendDatesWithEntries()
        return dates.some(d => set.has(d))
    }

    onWeekendJobChange(foremanHrRef: number, date: IsoDate, jobId: string) {
        const trimmed = this.normalizeJobId(jobId)
        const key = this.weekendPickerKey(foremanHrRef, date)
        this.updateWeekendPicker(key, () => ({ jobId: trimmed, phase: '', phases: [], loadingPhases: !!trimmed }))
        if (!trimmed) return

        this.services.data
            .post<JobPhaseOption[]>('api/ForemanSchedule/GetPhasesForJob', {
                vpJobId: trimmed,
                includeAllPhases: this.includeAllPhases(),
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: phases => {
                    this.updateWeekendPicker(key, current => ({
                        ...current,
                        phases: (phases ?? []).map(p => ({ ...p, remainingHours: p.remainingHours ?? 0 })),
                        loadingPhases: false,
                    }))
                },
                error: () => {
                    this.updateWeekendPicker(key, current => ({ ...current, phases: [], loadingPhases: false }))
                },
            })
    }

    onWeekendPhaseChange(foremanHrRef: number, date: IsoDate, phase: string, defaultHours: number) {
        const trimmed = (phase ?? '').trim()
        const key = this.weekendPickerKey(foremanHrRef, date)
        const picker = this.weekendPickers().get(key)
        const jobId = this.normalizeJobId(picker?.jobId)
        if (!jobId || !trimmed) return
        this.addWeekendEntry(foremanHrRef, date, jobId, trimmed, Math.max(defaultHours, 0))
    }

    onWeekendHoursChange(foremanHrRef: number, date: IsoDate, hours: number | string) {
        const parsed = typeof hours === 'number' ? hours : Number(hours)
        const value = isNaN(parsed) ? 0 : Math.max(parsed, 0)
        this.weekendEntriesByForeman.update(map => {
            const next = new Map(map)
            const list = (next.get(foremanHrRef) ?? []).map(e => (e.date === date ? { ...e, hours: value } : e))
            next.set(foremanHrRef, list)
            return next
        })
        this.persistWeekendEntries(foremanHrRef)
        this.reprojectAll()
    }

    clearWeekendEntry(foremanHrRef: number, date: IsoDate) {
        this.weekendEntriesByForeman.update(map => {
            const next = new Map(map)
            const list = (next.get(foremanHrRef) ?? []).filter(e => e.date !== date)
            next.set(foremanHrRef, list)
            return next
        })
        this.persistWeekendEntries(foremanHrRef)
        this.reprojectAll()
    }

    private addWeekendEntry(foremanHrRef: number, date: IsoDate, vpJobId: string, phase: string, hours: number) {
        const entry: SavedWeekendEntry = { id: newSegmentId(), foremanHrRef, date, vpJobId, phase, hours }
        this.weekendEntriesByForeman.update(map => {
            const next = new Map(map)
            const list = (next.get(foremanHrRef) ?? []).filter(e => e.date !== date)
            list.push(entry)
            next.set(foremanHrRef, list)
            return next
        })
        this.weekendPickers.update(map => {
            const next = new Map(map)
            next.delete(this.weekendPickerKey(foremanHrRef, date))
            return next
        })
        this.persistWeekendEntries(foremanHrRef)
        this.reprojectAll()
    }

    private weekendPickerKey(foremanHrRef: number, date: IsoDate): string {
        return `${foremanHrRef}|${date}`
    }

    private updateWeekendPicker(key: string, updater: (current: WeekendPickerState) => WeekendPickerState) {
        this.weekendPickers.update(map => {
            const next = new Map(map)
            const current = next.get(key) ?? { jobId: '', phase: '', phases: [], loadingPhases: false }
            next.set(key, updater(current))
            return next
        })
    }

    private seedWeekendEntries(saved: SavedWeekendEntry[]) {
        const map = new Map<number, SavedWeekendEntry[]>()
        for (const e of saved ?? []) {
            const date = isoDateStringForApi(e.date as string | Date | null | undefined)
            const list = map.get(e.foremanHrRef) ?? []
            list.push({ ...e, date, hours: e.hours ?? 0 })
            map.set(e.foremanHrRef, list)
        }
        this.weekendEntriesByForeman.set(map)
    }

    private persistWeekendEntries(foremanHrRef: number) {
        if (!this.canEdit()) return
        const entries = (this.weekendEntriesByForeman().get(foremanHrRef) ?? []).map(e => ({
            id: e.id,
            foremanHrRef,
            date: e.date,
            vpJobId: e.vpJobId,
            phase: e.phase,
            hours: e.hours,
        }))
        this.services.data
            .put('api/ForemanSchedule/SaveWeekendEntries', { foremanHrRef, entries })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => undefined,
                error: () => undefined,
            })
    }

    weekendCellTooltip(cell: CrewScheduleGridCellVm): string {
        const e = cell.weekendEntry
        if (e) return `Weekend work: ${e.vpJobId} / ${e.phase}\n${(e.hours ?? 0).toFixed(1)}h scheduled`
        if (cell.isHoliday) return cell.holidayName ? `Company holiday: ${cell.holidayName}` : 'Company holiday'
        return 'Manually schedule weekend work'
    }

    /** Opens the job/phase picker on an empty weekday cell so a new assignment can start on that day. */
    openDayPicker(foremanHrRef: number, date: IsoDate) {
        if (!this.canEdit()) return
        this.updateDayPicker(this.dayPickerKey(foremanHrRef, date), () => ({
            jobId: '',
            phase: '',
            phases: [],
            loadingPhases: false,
            days: null,
        }))
    }

    /** Cancels an open weekday assignment picker without creating a segment. */
    closeDayPicker(foremanHrRef: number, date: IsoDate) {
        this.dayPickers.update(map => {
            const next = new Map(map)
            next.delete(this.dayPickerKey(foremanHrRef, date))
            return next
        })
    }

    onDayJobChange(foremanHrRef: number, date: IsoDate, jobId: string) {
        const trimmed = this.normalizeJobId(jobId)
        const key = this.dayPickerKey(foremanHrRef, date)
        this.updateDayPicker(key, current => ({
            jobId: trimmed,
            phase: '',
            phases: [],
            loadingPhases: !!trimmed,
            days: current.days ?? null,
        }))
        if (!trimmed) return

        this.services.data
            .post<JobPhaseOption[]>('api/ForemanSchedule/GetPhasesForJob', {
                vpJobId: trimmed,
                includeAllPhases: this.includeAllPhases(),
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: phases => {
                    this.updateDayPicker(key, current => ({
                        ...current,
                        phases: (phases ?? []).map(p => ({ ...p, remainingHours: p.remainingHours ?? 0 })),
                        loadingPhases: false,
                    }))
                },
                error: () => {
                    this.updateDayPicker(key, current => ({ ...current, phases: [], loadingPhases: false }))
                },
            })
    }

    /** Stages the chosen phase in the picker. The assignment is created when the user confirms with "Add". */
    onDayPhaseChange(foremanHrRef: number, date: IsoDate, phase: string) {
        const trimmed = (phase ?? '').trim()
        const key = this.dayPickerKey(foremanHrRef, date)
        this.updateDayPicker(key, current => ({ ...current, phase: trimmed }))
    }

    /** Stages the optional forced day count entered in the picker. */
    onDayDaysChange(foremanHrRef: number, date: IsoDate, days: number | string | null) {
        const key = this.dayPickerKey(foremanHrRef, date)
        const parsed = this.parseForcedDays(days)
        this.updateDayPicker(key, current => ({ ...current, days: parsed }))
    }

    /** Creates the assignment from the staged job/phase/days once the user confirms. */
    confirmDayAssignment(foremanHrRef: number, date: IsoDate) {
        if (!this.canEdit()) return
        const key = this.dayPickerKey(foremanHrRef, date)
        const picker = this.dayPickers().get(key)
        const jobId = this.normalizeJobId(picker?.jobId)
        const phase = (picker?.phase ?? '').trim()
        if (!jobId || !phase) return

        this.addSegmentForForeman(foremanHrRef, date, jobId, phase, this.parseForcedDays(picker?.days))
        this.closeDayPicker(foremanHrRef, date)
    }

    /** Opens a modal to assign any visible foreman to an empty job group. */
    openJobAssignmentModal(jobId: string) {
        if (!this.canEdit()) return
        const normalizedJobId = this.normalizeJobId(jobId)
        if (!normalizedJobId) return

        const foremen = this.filteredForemanRows().map(r => ({
            hrRef: r.hrRef,
            name: r.name,
            jobTitle: r.jobTitle,
            segments: r.state.segments.map(s => this.toSegmentSpan(s)),
        }))
        if (!foremen.length) return

        const ref = this.services.modals.open(
            ForemanScheduleJobAssignmentModalComponent,
            ModalSize.medium,
            ModalBackDrop.darkAndInactive,
        )
        const modal = ref.componentInstance as ForemanScheduleJobAssignmentModalComponent
        modal.initFromContext({
            vpJobId: normalizedJobId,
            jobLabel: this.jobLabel(normalizedJobId),
            foremen,
            defaultStartDate: this.gridStart(),
            includeAllPhases: this.includeAllPhases(),
        })

        ref.result.then(
            (result: JobAssignmentModalResult) => {
                this.addSegmentForForeman(
                    result.foremanHrRef,
                    result.startDate,
                    normalizedJobId,
                    result.phase,
                    result.forcedDays,
                )
                this.expandedForemanHrRefs.update(set => {
                    const next = new Set(set)
                    next.add(result.foremanHrRef)
                    return next
                })
            },
            () => undefined,
        )
    }

    /** Adds a new segment for a foreman and re-runs projection (and persist when complete). */
    private addSegmentForForeman(
        foremanHrRef: number,
        date: IsoDate,
        jobId: string,
        phase: string,
        forcedDays: number | null,
    ) {
        const normalizedJobId = this.normalizeJobId(jobId)
        const trimmedPhase = (phase ?? '').trim()
        if (!normalizedJobId || !trimmedPhase) return

        const segment: CrewScheduleSegment = {
            id: newSegmentId(),
            vpJobId: normalizedJobId,
            phase: trimmedPhase,
            startDate: date,
            days: [],
            startingRemainingHours: 0,
            overThresholdHours: 0,
            endDateExclusive: null,
            projectedFinishDate: null,
            forcedDays,
        }

        this.updateForemanState(foremanHrRef, current => ({
            ...current,
            segments: [...current.segments, segment].sort((a, b) => a.startDate.localeCompare(b.startDate)),
            loadingProjection: true,
            error: null,
        }))

        this.runFullProjection(foremanHrRef)
    }

    /** Normalizes a user-entered day count to a positive integer, or null when blank/invalid. */
    private parseForcedDays(value: number | string | null | undefined): number | null {
        if (value == null || value === '') return null
        const n = Math.floor(Number(value))
        return Number.isFinite(n) && n > 0 ? n : null
    }

    private dayPickerKey(foremanHrRef: number, date: IsoDate): string {
        return `${foremanHrRef}|${date}`
    }

    private updateDayPicker(key: string, updater: (current: WeekendPickerState) => WeekendPickerState) {
        this.dayPickers.update(map => {
            const next = new Map(map)
            const current = next.get(key) ?? { jobId: '', phase: '', phases: [], loadingPhases: false }
            next.set(key, updater(current))
            return next
        })
    }

    clearFromDay(foremanHrRef: number, segmentId: string, fromDate: IsoDate) {
        const state = this.foremanStates().get(foremanHrRef)
        if (!state) return

        const segment = state.segments.find(s => s.id === segmentId)
        if (!segment) return

        this.updateForemanState(foremanHrRef, current => {
            let segments: CrewScheduleSegment[]
            if (fromDate <= segment.startDate) {
                // Clearing from the first day removes the assignment entirely.
                segments = current.segments.filter(s => s.id !== segmentId)
            } else {
                // Truncate this assignment so the crew stops on/after fromDate (persisted boundary).
                const keptDays = segment.days.filter(d => d.date < fromDate)
                segments = current.segments.map(s =>
                    s.id === segmentId
                        ? {
                              ...s,
                              endDateExclusive: fromDate,
                              days: keptDays,
                              projectedFinishDate: this.computeSegmentFinishDate(keptDays),
                          }
                        : s,
                )
            }

            return {
                ...current,
                segments,
                loadingProjection: true,
                error: null,
            }
        })

        this.runFullProjection(foremanHrRef)
    }

    private readStoredGroupBy(): ForemanScheduleGroupBy {
        try {
            const stored = localStorage.getItem(ForemanScheduleCrewScheduleTabComponent.GROUP_BY_STORAGE_KEY)
            if (stored === 'job' || stored === 'division') return stored
        } catch {
            /* ignore storage failures */
        }
        return 'division'
    }

    private buildDivisionForemanGroups(rows: CrewScheduleForemanRowVm[]): CrewScheduleForemanGroupVm[] {
        const groups = new Map<string, CrewScheduleForemanGroupVm>()
        for (const row of rows) {
            const key = row.divisionId ?? ''
            let group = groups.get(key)
            if (!group) {
                const divisionName = row.divisionName || 'No Division'
                group = {
                    key: key || 'no-division',
                    label: divisionName,
                    groupJobId: null,
                    divisionId: row.divisionId,
                    rows: [],
                }
                groups.set(key, group)
            }
            group.rows.push(row)
        }

        return [...groups.values()].sort((a, b) => {
            if (!a.divisionId && b.divisionId) return 1
            if (a.divisionId && !b.divisionId) return -1
            return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        })
    }

    private buildJobForemanGroups(rows: CrewScheduleForemanRowVm[]): CrewScheduleForemanGroupVm[] {
        const jobOrderIndex = buildJobOrderIndex(this.activeJobs())
        const groups = new Map<string, CrewScheduleForemanGroupVm>()
        this.seedActiveJobGroups(groups)

        for (const row of rows) {
            const jobIds = this.collectJobIdsFromRow(row)

            if (jobIds.size === 0) {
                let group = groups.get(FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY)
                if (!group) {
                    group = {
                        key: FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY,
                        label: 'No job scheduled',
                        groupJobId: null,
                        divisionId: null,
                        rows: [],
                    }
                    groups.set(FOREMAN_SCHEDULE_NO_JOB_GROUP_KEY, group)
                }
                group.rows.push(row)
                continue
            }

            for (const jobId of jobIds) {
                const key = `job-${jobId}`
                let group = groups.get(key)
                if (!group) {
                    group = {
                        key,
                        label: this.jobLabel(jobId),
                        groupJobId: jobId,
                        divisionId: null,
                        rows: [],
                    }
                    groups.set(key, group)
                }
                group.rows.push(this.maskCrewScheduleRowForJob(row, jobId))
            }
        }

        return [...groups.values()].sort((a, b) =>
            compareForemanScheduleJobGroups(
                { groupJobId: a.groupJobId },
                { groupJobId: b.groupJobId },
                jobOrderIndex,
            ),
        )
    }

    /** Pre-create a group for every active job so unscheduled jobs are visible when grouping by job. */
    private seedActiveJobGroups(groups: Map<string, CrewScheduleForemanGroupVm>): void {
        for (const job of this.activeJobs()) {
            const jobId = this.normalizeJobId(job.id)
            if (!jobId) continue
            const key = `job-${jobId}`
            groups.set(key, {
                key,
                label: `${jobId} — ${job.name}`,
                groupJobId: jobId,
                divisionId: null,
                rows: [],
            })
        }
    }

    private collectJobIdsFromRow(row: CrewScheduleForemanRowVm): Set<string> {
        const jobIds = new Set<string>()
        for (const cell of row.gridCells) {
            if (cell.type === 'day' && cell.assignment && cell.assignment.hoursScheduled > 0) {
                const jobId = this.normalizeJobId(cell.assignment.vpJobId)
                if (jobId) jobIds.add(jobId)
            } else if (cell.type === 'weekend-day' && cell.weekendEntry) {
                const jobId = this.normalizeJobId(cell.weekendEntry.vpJobId)
                if (jobId) jobIds.add(jobId)
            }
        }
        return jobIds
    }

    private maskCrewScheduleRowForJob(row: CrewScheduleForemanRowVm, jobId: string): CrewScheduleForemanRowVm {
        const normalizedJobId = this.normalizeJobId(jobId)
        return {
            ...row,
            gridCells: row.gridCells.map(cell => this.maskCrewScheduleCellForJob(cell, normalizedJobId, row)),
        }
    }

    private maskCrewScheduleCellForJob(
        cell: CrewScheduleGridCellVm,
        groupJobId: string,
        row: CrewScheduleForemanRowVm,
    ): CrewScheduleGridCellVm {
        if (cell.type === 'day') {
            const assignment = cell.assignment
            if (assignment && assignment.hoursScheduled > 0) {
                const cellJobId = this.normalizeJobId(assignment.vpJobId)
                if (cellJobId && cellJobId !== groupJobId) {
                    return {
                        ...cell,
                        assignment: null,
                        segmentId: null,
                        isOverEstimate: false,
                        isOverHours: false,
                        isShared: false,
                        showAddButton: false,
                        showClearButton: false,
                        dayPicker: null,
                        maskedJobId: cellJobId,
                    }
                }
            }
            return { ...cell, maskedJobId: null }
        }

        if (cell.type === 'weekend-day') {
            const entry = cell.weekendEntry
            if (entry) {
                const entryJobId = this.normalizeJobId(entry.vpJobId)
                if (entryJobId && entryJobId !== groupJobId) {
                    return {
                        ...cell,
                        weekendEntry: null,
                        weekendPicker: null,
                        showClearButton: false,
                        maskedJobId: entryJobId,
                    }
                }
            }
            return { ...cell, maskedJobId: null }
        }

        if (cell.type === 'weekend') {
            const hasEntriesForJob = row.gridCells.some(
                c =>
                    c.type === 'weekend-day' &&
                    c.weekendKey === cell.weekendKey &&
                    !!c.weekendEntry &&
                    this.normalizeJobId(c.weekendEntry.vpJobId) === groupJobId,
            )
            return {
                ...cell,
                weekendHasEntries: hasEntriesForJob,
            }
        }

        return cell
    }

    normalizeJobId(jobId: string | null | undefined): string {
        return (jobId ?? '').trim()
    }

    compareJobIds = (a: string, b: string): boolean => this.normalizeJobId(a) === this.normalizeJobId(b)

    comparePhases = (a: string, b: string): boolean => (a ?? '').trim() === (b ?? '').trim()

    isActiveJob(jobId: string): boolean {
        const id = this.normalizeJobId(jobId)
        return this.activeJobs().some(j => this.normalizeJobId(j.id) === id)
    }

    jobLabel(jobId: string): string {
        const id = this.normalizeJobId(jobId)
        if (!id) return ''
        const job = this.activeJobs().find(j => this.normalizeJobId(j.id) === id)
        return job ? `${id} — ${job.name}` : id
    }

    dayPickerOverlapNote(row: CrewScheduleForemanRowVm, cell: CrewScheduleGridCellVm): string | null {
        const overlaps = this.dayPickerOverlaps(row, cell)
        return overlaps ? compactSegmentOverlapNote(overlaps) : null
    }

    dayPickerOverlapTooltip(row: CrewScheduleForemanRowVm, cell: CrewScheduleGridCellVm): string {
        const overlaps = this.dayPickerOverlaps(row, cell)
        return overlaps
            ? describeSegmentOverlaps(overlaps, iso => this.formatShortDate(iso)) ?? ''
            : ''
    }

    private dayPickerOverlaps(row: CrewScheduleForemanRowVm, cell: CrewScheduleGridCellVm) {
        const picker = cell.dayPicker
        if (!picker?.jobId || !cell.date) return null
        const spans = row.state.segments.map(s => this.toSegmentSpan(s))
        const overlaps = findSegmentOverlaps(spans, picker.jobId, picker.phase ?? '', cell.date)
        return overlaps.length ? overlaps : null
    }

    private toSegmentSpan(segment: CrewScheduleSegment): ForemanSegmentSpan {
        const workedDays = segment.days.filter(d => d.hoursScheduled > 0)
        let lastWorkedDate: IsoDate | null = null
        for (const day of workedDays) {
            const date = isoDateStringForApi(day.date as string | Date | null | undefined)
            if (date) lastWorkedDate = date
        }
        return {
            vpJobId: segment.vpJobId,
            phase: segment.phase,
            startDate: segment.startDate,
            lastWorkedDate,
            endDateExclusive: segment.endDateExclusive,
        }
    }

    phaseLabel(option: JobPhaseOption): string {
        const desc = (option.description ?? '').trim()
        return desc && desc !== 'Unknown Phase' ? `${option.phase} — ${desc}` : option.phase
    }

    /** Shared pool summary for a job+phase from the latest projection, if any crews are assigned to it. */
    pool(vpJobId: string, phase: string): JobPhasePool | undefined {
        return this.projectionPools().get(crewScheduleJobPhaseKey(vpJobId, phase))
    }

    cellTooltip(cell: CrewScheduleGridCellVm): string {
        if (cell.type === 'weekend') return 'Weekend — no scheduled work'
        if (cell.isHoliday) {
            return cell.holidayName ? `Company holiday: ${cell.holidayName}` : 'Company holiday'
        }
        const a = cell.assignment
        if (!a || a.hoursScheduled <= 0) {
            if (a && a.availableCrewCount === 0 && a.totalCrewCount > 0) {
                return 'No crew available (PTO or time off)'
            }
            return ''
        }
        const crewNote =
            a.availableCrewCount < a.totalCrewCount
                ? `${a.availableCrewCount} of ${a.totalCrewCount} crew available`
                : `${a.availableCrewCount} crew`
        const remainingNote =
            a.remainingHoursAtStart != null && a.remainingHoursAtEnd != null
                ? `Shared pool: ${a.remainingHoursAtStart.toFixed(1)}h → ${a.remainingHoursAtEnd.toFixed(1)}h`
                : ''
        const sharedNote =
            (a.sharedCrewCount ?? 1) > 1 ? `\nShared with ${(a.sharedCrewCount ?? 1) - 1} other crew(s) today` : ''
        const overNote = cell.isOverEstimate ? '\nOver estimated hours' : ''
        const overHoursNote = cell.isOverHours ? '\nOver remaining hours (forced day)' : ''
        return `${a.vpJobId} / ${a.phase}\n${a.hoursScheduled}h scheduled${remainingNote ? `\n${remainingNote}` : ''}\n${crewNote}${sharedNote}${overNote}${overHoursNote}`
    }

    private loadContext() {
        this.isLoading.set(true)
        this.loadError.set(null)

        const start = todayIso()
        const end = addDaysIso(start, CREW_SCHEDULE_CONTEXT_DAYS)
        const params = new HttpParams().set('startDate', start).set('endDate', end)

        this.cancelableRequest(
            'crew-schedule-context',
            true,
            this.services.data
                .load(
                    true,
                    false,
                    this.services.data
                        .get<{ ready: boolean }>(
                            'api/ForemanSchedule/WarmCrewScheduleProductivityCache',
                            new HttpParams(),
                            Object,
                        )
                        .pipe(
                            switchMap(() =>
                                this.services.data.get<CrewScheduleContext>(
                                    'api/ForemanSchedule/GetCrewScheduleContext',
                                    params,
                                    Object,
                                ),
                            ),
                        ),
                    this.services.data.divisions.queryAll(),
                )
                .subscribe({
                    next: ([data, divisions]) => {
                        this.hasLoaded = true
                        // Keep isLoading true until the initial full projection resolves (see
                        // runFullProjection); clearing it here would flash an empty grid while the
                        // slower ProjectFullCrewSchedule call is still running.
                        this.divisionOptions.set(
                            ((divisions ?? []) as Division[])
                                .filter(d => !d.archived)
                                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
                        )
                        const normalized = this.normalizeContext(data as CrewScheduleContext)
                        this.context.set(normalized)
                        this.expandedForemanHrRefs.set(new Set((normalized.foremen ?? []).map(f => f.hrRef)))
                        this.foremanStates.update(map => {
                            const next = new Map(map)
                            for (const f of normalized.foremen ?? []) {
                                if (!next.has(f.hrRef)) next.set(f.hrRef, this.createEmptyForemanState())
                            }
                            return next
                        })
                    },
                    error: () => {
                        this.isLoading.set(false)
                        this.loadError.set('Failed to load crew schedule context.')
                    },
                }),
        )
    }

    /**
     * Projects every foreman's segments together against shared per job+phase hour pools and merges the
     * returned day assignments back into each segment. When `changedForemanHrRef` is provided, that
     * foreman's segment definitions are persisted after the projection completes.
     */
    private runFullProjection(changedForemanHrRef?: number) {
        const request = {
            startDate: this.gridStart(),
            maxProjectionDays: CREW_SCHEDULE_CONTEXT_DAYS,
            hoursPerPerson: 10,
            segments: this.buildSegmentInputs(),
            weekendEntries: this.weekendEntriesToApi(),
            reassignments: this.reassignmentsToApi(),
        }

        this.services.data
            .post<FullCrewScheduleProjection>('api/ForemanSchedule/ProjectFullCrewSchedule', request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: projection => {
                    this.applyProjection(projection)
                    this.isLoading.set(false)
                    if (changedForemanHrRef != null) this.persistSegments(changedForemanHrRef)
                },
                error: () => {
                    this.isLoading.set(false)
                    this.foremanStates.update(map => {
                        const next = new Map(map)
                        for (const [hrRef, state] of next) {
                            next.set(hrRef, {
                                ...state,
                                loadingProjection: false,
                                error:
                                    changedForemanHrRef == null || hrRef === changedForemanHrRef
                                        ? 'Failed to project crew schedule.'
                                        : state.error,
                            })
                        }
                        return next
                    })
                },
            })
    }

    /** Re-runs the global projection for all foremen (used after weekend or reassignment changes). */
    private reprojectAll() {
        this.foremanStates.update(map => {
            const next = new Map(map)
            for (const [hrRef, state] of next) {
                if (state.segments.length) next.set(hrRef, { ...state, loadingProjection: true })
            }
            return next
        })
        this.runFullProjection()
    }

    /** Flattens all foremen's segment definitions into the global projection request payload. */
    private buildSegmentInputs(): FullScheduleSegmentInput[] {
        const inputs: FullScheduleSegmentInput[] = []
        for (const [hrRef, state] of this.foremanStates()) {
            state.segments.forEach((seg, index) => {
                inputs.push({
                    clientId: seg.id,
                    foremanHrRef: hrRef,
                    vpJobId: seg.vpJobId,
                    phase: seg.phase,
                    startDate: seg.startDate,
                    sortOrder: index,
                    endDateExclusive: seg.endDateExclusive ?? null,
                    forcedDays: seg.forcedDays ?? null,
                })
            })
        }
        return inputs
    }

    private weekendEntriesToApi(): SavedWeekendEntry[] {
        const entries: SavedWeekendEntry[] = []
        for (const [foremanHrRef, list] of this.weekendEntriesByForeman()) {
            for (const e of list) {
                entries.push({
                    id: e.id,
                    foremanHrRef,
                    date: e.date,
                    vpJobId: e.vpJobId,
                    phase: e.phase,
                    hours: e.hours ?? 0,
                })
            }
        }
        return entries
    }

    /** Merges a global projection response back into local segment days and the shared pool summary. */
    private applyProjection(projection: FullCrewScheduleProjection) {
        const pools = new Map<string, JobPhasePool>()
        for (const p of projection.pools ?? []) {
            pools.set(crewScheduleJobPhaseKey(p.vpJobId, p.phase), p)
        }
        this.projectionPools.set(pools)

        const bySegment = new Map<string, ProjectedSegment>()
        for (const f of projection.foremen ?? []) {
            for (const s of f.segments ?? []) {
                if (s.clientId) bySegment.set(s.clientId, s)
            }
        }

        this.foremanStates.update(map => {
            const next = new Map(map)
            for (const [hrRef, state] of next) {
                const segments = state.segments.map(seg => {
                    const proj = bySegment.get(seg.id)
                    if (!proj) return seg
                    return {
                        ...seg,
                        startDate: isoDateStringForApi(proj.startDate as string | Date | null | undefined) || seg.startDate,
                        days: this.normalizeDays(proj.days),
                        startingRemainingHours: proj.startingRemainingHours ?? seg.startingRemainingHours,
                        overThresholdHours: Math.max(proj.overThresholdHours ?? 0, 0),
                        projectedFinishDate: proj.projectedFinishDate
                            ? isoDateStringForApi(proj.projectedFinishDate as string | Date | null | undefined)
                            : null,
                    }
                })
                next.set(hrRef, { ...state, segments, loadingProjection: false })
            }
            return next
        })
    }

    private normalizeDays(days: CrewScheduleDayAssignment[]): CrewScheduleDayAssignment[] {
        return (days ?? []).map(d => ({
            ...d,
            date: isoDateStringForApi(d.date as string | Date | null | undefined),
            remainingHoursAtStart: d.remainingHoursAtStart ?? 0,
            remainingHoursAtEnd: d.remainingHoursAtEnd ?? 0,
            sharedCrewCount: d.sharedCrewCount ?? 1,
        }))
    }

    private computeSegmentFinishDate(days: CrewScheduleDayAssignment[]): IsoDate | null {
        const workDays = days.filter(d => d.hoursScheduled > 0)
        if (workDays.length === 0) return null
        const last = workDays[workDays.length - 1]
        return last.remainingHoursAtEnd <= 0 ? last.date : null
    }

    private updateForemanState(hrRef: number, updater: (current: CrewScheduleForemanState) => CrewScheduleForemanState) {
        this.foremanStates.update(map => {
            const next = new Map(map)
            const current = next.get(hrRef) ?? this.createEmptyForemanState()
            next.set(hrRef, updater(current))
            return next
        })
    }

    private createEmptyForemanState(): CrewScheduleForemanState {
        return {
            segments: [],
            loadingProjection: false,
            error: null,
        }
    }

    private normalizeContext(data: CrewScheduleContext): CrewScheduleContext {
        return {
            ...data,
            dateRangeStart: isoDateStringForApi(data.dateRangeStart as string | Date | null | undefined),
            dateRangeEnd: isoDateStringForApi(data.dateRangeEnd as string | Date | null | undefined),
            foremen: (data.foremen ?? []).map(f => ({
                ...f,
                members: (f.members ?? []).map(m => ({
                    ...m,
                    timeOff: (m.timeOff ?? []).map(t => ({
                        ...t,
                        startDate: isoDateStringForApi(t.startDate as string | Date | null | undefined),
                        endDate: isoDateStringForApi(t.endDate as string | Date | null | undefined),
                    })),
                })),
            })),
            holidays: (data.holidays ?? []).map(h => ({
                ...h,
                date: isoDateStringForApi(h.date as string | Date | null | undefined),
            })),
        }
    }

    private buildSummaryText(state: CrewScheduleForemanState): string {
        if (state.loadingProjection) return 'Calculating projection…'
        if (state.segments.length === 0) return 'Select a day, then pick a job and phase to project crew hours.'

        const parts = state.segments.map(seg => {
            const lastWork = [...seg.days].reverse().find(d => d.hoursScheduled > 0)
            const pool = this.pool(seg.vpJobId, seg.phase)
            const remainingLeft = lastWork
                ? lastWork.remainingHoursAtEnd
                : pool?.remainingAfterProjection ?? seg.startingRemainingHours
            const sharedNote = pool && pool.crewCount > 1 ? ` (shared by ${pool.crewCount} crews)` : ''
            const overNote = seg.overThresholdHours > 0 ? `; ${seg.overThresholdHours.toFixed(1)}h over estimate` : ''
            const finish = seg.projectedFinishDate
                ? `finish ${this.formatShortDate(seg.projectedFinishDate)}`
                : lastWork
                  ? `${remainingLeft.toFixed(1)}h left after segment`
                  : 'no projected work'
            return `${seg.vpJobId}/${seg.phase}: ${remainingLeft.toFixed(1)}h pool remaining${sharedNote}; ${finish}${overNote}`
        })
        return parts.join(' · ')
    }

    private buildWarningText(state: CrewScheduleForemanState): string | null {
        if (
            state.segments.some(
                s => s.startingRemainingHours > 0 && !s.days.some(d => d.hoursScheduled > 0),
            )
        ) {
            return 'An assignment is fully overridden by another assignment on the same days.'
        }

        const sortedSegments = [...state.segments].sort((a, b) => a.startDate.localeCompare(b.startDate))
        if (
            sortedSegments.some(seg => {
                const hasWork = seg.days.some(d => d.hoursScheduled > 0)
                if (!hasWork || seg.projectedFinishDate || seg.endDateExclusive) return false
                return sortedSegments.some(other => other !== seg && other.startDate > seg.startDate)
            })
        ) {
            return 'An assignment may end early because another assignment starts before it finishes.'
        }

        // A zero-hour phase intentionally added via "show all phases" still projects a single working day,
        // so only warn when a segment both lacks remaining hours and produced no scheduled work.
        if (state.segments.some(s => s.startingRemainingHours <= 0 && !s.days.some(d => d.hoursScheduled > 0))) {
            return 'One or more segments have no projected hours remaining.'
        }
        if (state.segments.some(s => s.days.some(d => d.isOverHours))) {
            return 'One or more assignments are forced beyond their remaining hours (shown in red).'
        }
        if (state.segments.some(s => s.overThresholdHours > 0)) {
            return 'One or more phases are projected beyond their estimated hours (shown in red).'
        }
        return null
    }

    private reassignmentsToApi() {
        return (this.reassignments() ?? []).map(r => ({
            memberHrRef: r.memberHrRef,
            fromForemanHrRef: r.fromForemanHrRef,
            toForemanHrRef: r.toForemanHrRef,
            startDate: r.startDate,
            endDate: r.endDate,
        }))
    }

    /** Replays persisted segment definitions into local state, then runs one global projection. */
    private rebuildSavedSegments(saved: SavedForemanSegment[]) {
        if (!this.context()) return

        const byForeman = new Map<number, SavedForemanSegment[]>()
        for (const segment of saved ?? []) {
            const list = byForeman.get(segment.foremanHrRef) ?? []
            list.push(segment)
            byForeman.set(segment.foremanHrRef, list)
        }

        this.foremanStates.update(map => {
            const next = new Map(map)
            for (const [hrRef, state] of next) {
                const defs = (byForeman.get(hrRef) ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)
                const segments: CrewScheduleSegment[] = defs.map(d => ({
                    id: newSegmentId(),
                    vpJobId: this.normalizeJobId(d.vpJobId),
                    phase: (d.phase ?? '').trim(),
                    startDate: isoDateStringForApi(d.startDate as string | Date | null | undefined),
                    days: [],
                    startingRemainingHours: d.startingRemainingHours ?? 0,
                    overThresholdHours: 0,
                    endDateExclusive: d.endDateExclusive
                        ? isoDateStringForApi(d.endDateExclusive as string | Date | null | undefined) || null
                        : null,
                    projectedFinishDate: null,
                    forcedDays: d.forcedDays ?? null,
                }))
                next.set(hrRef, {
                    ...state,
                    segments,
                    loadingProjection: segments.length > 0,
                    error: null,
                })
            }
            return next
        })

        this.runFullProjection()
    }

    /** Auto-saves the current segment definitions for a foreman (replace-per-foreman on the server). */
    private persistSegments(foremanHrRef: number) {
        if (!this.canEdit()) return

        const state = this.foremanStates().get(foremanHrRef)
        const segments = (state?.segments ?? []).map((s, i) => ({
            foremanHrRef,
            vpJobId: s.vpJobId,
            phase: s.phase,
            startDate: s.startDate,
            sortOrder: i,
            startingRemainingHours: s.startingRemainingHours,
            endDateExclusive: s.endDateExclusive,
            forcedDays: s.forcedDays ?? null,
        }))

        this.services.data
            .put('api/ForemanSchedule/SaveForemanSegments', { foremanHrRef, segments })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => undefined,
                error: () => undefined,
            })
    }

    private formatScheduledOffDaysLabel(count: number): string {
        const dayWord = count === 1 ? 'day' : 'days'
        return `${count} ${dayWord} scheduled off in next ${CREW_SCHEDULE_WEEKDAYS} work days`
    }

    private countCrewScheduledOffDays(
        foreman: { hrRef: number; members: CrewScheduleMember[] },
        allForemen: { hrRef: number; members: CrewScheduleMember[] }[],
        weekdays: IsoDate[],
        reassignments: CrewMemberReassignment[],
    ): number {
        let total = 0
        for (const date of weekdays) {
            const effective = resolveEffectiveMembers(foreman, date, allForemen, reassignments)
            for (const member of effective) {
                if (this.memberTimeOffOn(member, date)) total++
            }
        }
        return total
    }

    /** Returns the time-off entry covering `date` for this member, or null if they are working. */
    private memberTimeOffOn(member: CrewScheduleMember, date: IsoDate): TimeOffEntry | null {
        for (const entry of member.timeOff ?? []) {
            const start = isoDateStringForApi(entry.startDate as string | Date | null | undefined)
            const end = isoDateStringForApi(entry.endDate as string | Date | null | undefined)
            if (start && end && start <= date && end >= date) return entry
        }
        return null
    }

    private initials(name: string): string {
        const words = (name ?? '').replace(/,/g, ' ').split(/\s+/).filter(Boolean)
        if (!words.length) return '?'
        const letters = words.slice(0, 2).map(w => w[0]?.toUpperCase() ?? '')
        return letters.join('') || '?'
    }

    offStripTooltip(cell: CrewScheduleGridCellVm): string {
        const off = cell.offMembers ?? []
        if (!off.length) return ''
        const lines = off.map(m => `${m.name} — ${m.leaveType} (${m.status})`)
        return `Off this day:\n${lines.join('\n')}`
    }

    /** Off crew members (effective roster) for a foreman on a given weekday. */
    private buildOffMembers(
        foreman: CrewScheduleForeman,
        date: IsoDate,
        allForemen: CrewScheduleForeman[],
        reassignments: CrewMemberReassignment[],
    ): CrewOffMemberVm[] {
        const off: CrewOffMemberVm[] = []
        for (const member of resolveEffectiveMembers(foreman, date, allForemen, reassignments)) {
            const entry = this.memberTimeOffOn(member, date)
            if (!entry) continue
            off.push({
                hrRef: member.hrRef,
                name: member.name,
                initials: this.initials(member.name),
                status: entry.status,
                leaveType: entry.leaveType?.trim() || 'Time off',
            })
        }
        return off
    }

    private formatShortDate(iso: IsoDate): string {
        const [y, m, d] = iso.split('-').map(n => parseInt(n, 10))
        const dt = new Date(Date.UTC(y, m - 1, d))
        return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    }
}
